import type { ReactNode } from "react";
import styles from "./ds.module.css";

export type BadgeTone = "neutral" | "brand" | "indigo" | "success" | "warning" | "danger" | "info";

/** Small tinted label. Status is carried by a dot plus a word, so it survives greyscale. */
export function Badge({ tone = "neutral", uppercase = false, dot = false, children, title }: {
    tone?: BadgeTone; uppercase?: boolean; dot?: boolean; children: ReactNode; title?: string;
}) {
    return (
        <span className={`${styles.badge} ${styles[tone]} ${uppercase ? styles.badgeUpper : ""}`} title={title}>
            {dot && <span aria-hidden="true">●&nbsp;</span>}{children}
        </span>
    );
}

/** OP's launch status at shutdown: Enabled (green) or Paused (amber); unknown reads "—". */
export function LaunchStatusBadge({ active }: { active?: boolean | null }) {
    if (active === null || active === undefined) return <span className={styles.muted}>—</span>;
    return <Badge tone={active ? "success" : "warning"} dot>{active ? "Enabled" : "Paused"}</Badge>;
}

/** A grey token: plain for words, mono for machine values operators paste into search. */
export function Tag({ mono = false, children, title }: { mono?: boolean; children: ReactNode; title?: string }) {
    return <span className={`${styles.tag} ${mono ? styles.tagMono : ""}`} title={title}>{children}</span>;
}

export function IdChip({ label, value }: { label: string; value: ReactNode }) {
    return <span className={styles.idChip}>{label} {value}</span>;
}

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

export function Avatar({ name, size = 20 }: { name: string; size?: number }) {
    return (
        <span className={styles.avatar} style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.46)) }} title={name}>
            {initials(name)}
        </span>
    );
}
